import type { ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import { useIsMobile } from '../../../ds/hooks';
import styles from './SplitAuthCard.module.css';

export interface SplitAuthCardProps {
  /** Eyebrow of the hero („DAMOKLES Suite", the organization name). */
  eyebrow: string;
  headline: string;
  /** Hero sentence below the headline (hidden on phones). */
  subline?: ReactNode | undefined;
  /** Headline of the phone hero (Admin M1 „DAMOKLES für Ihr Unternehmen einrichten"); defaults to `headline`. */
  mobileHeadline?: string | undefined;
  /** 540 (Admin 01) or 520 (Nutzer 01): width of the form column. */
  width: 540 | 520;
  children: ReactNode;
}

/**
 * The split sign-up screen of Admin 01 / Nutzer 01: navy hero with the frame's gradient (the frames' `image-slot`
 * has no asset) and the form column; below 768 px the hero shrinks to 210 px above the form (M1).
 */
export function SplitAuthCard({
  eyebrow,
  headline,
  subline,
  mobileHeadline,
  width,
  children,
}: SplitAuthCardProps) {
  const mobile = useIsMobile();
  return (
    <div
      className={clsx(styles.card, width === 520 && styles.w520)}
      style={mobile ? undefined : { gridTemplateColumns: `1fr ${width}px` }}
    >
      <div className={styles.hero}>
        <div className={width === 520 ? styles.gradient520 : styles.gradient540} aria-hidden="true" />
        <div className={styles.heroText}>
          <span className={styles.heroEyebrow}>{eyebrow}</span>
          <span className={styles.headline}>{mobile ? (mobileHeadline ?? headline) : headline}</span>
          {subline ? <span className={styles.subline}>{subline}</span> : null}
        </div>
      </div>
      <main className={styles.panel}>{children}</main>
    </div>
  );
}

export interface SplitAuthIntroProps {
  eyebrow: string;
  title: string;
  text?: ReactNode | undefined;
}

/** Title block of the form column: eyebrow 10 px, display title 30 px, 13 px text. */
export function SplitAuthIntro({ eyebrow, title, text }: SplitAuthIntroProps) {
  return (
    <div className={styles.intro}>
      <span className={styles.introEyebrow}>{eyebrow}</span>
      <h1 className={styles.introTitle}>{title}</h1>
      {text ? <p className={styles.introText}>{text}</p> : null}
    </div>
  );
}
